import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { backLink } from "../components/common.js";
import { onUnmount } from "../core/lifecycle.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { spinner, errorState, toast } from "../components/ui.js";
import { reportButton } from "../components/report.js";
import { relativeDay, fmtVolume, fmtDuration, parseDate } from "../lib/format.js";
import { avatar } from "./Friends.js";

export default function FriendProfile({ id }) {
  const root = h("div", { class: "screen" });
  let data = null;
  let timer = null;
  load();

  async function load() {
    setChildren(root, backLink("Friends", "/friends"), spinner());
    try {
      data = await api.friendProfile(id);
      render();
    } catch (e) {
      setChildren(root, backLink("Friends", "/friends"), errorState(e.message, () => navigate("/friends")));
    }
  }

  function render() {
    const u = data.user;
    if (timer) { clearInterval(timer); timer = null; }

    setChildren(root,
      backLink("Friends", "/friends"),
      h("div", { class: "card hstack", style: { gap: "14px", alignItems: "center" } },
        avatar(u, 56),
        h("div", { class: "grow" },
          h("div", { class: "hstack", style: { gap: "8px", alignItems: "center" } },
            h("div", { style: { fontWeight: 800, fontSize: "1.2rem" } }, u.display_name || u.username),
            data.is_mutual ? h("span", { class: "badge", style: { background: "var(--accent)", color: "#fff" } }, "Friends") : null
          ),
          h("div", { class: "dim" }, `@${u.username}`, u.is_private ? " · 🔒 private" : ""),
          data.follows_me ? h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, "Follows you") : null
        )
      ),

      followButton(),

      data.viewable ? viewableBody() : lockedBody(),

      h("div", { class: "center", style: { marginTop: "22px" } },
        reportButton({ targetType: "user", targetId: Number(id), name: `@${u.username}` }))
    );
  }

  function followButton() {
    if (data.following === "accepted")
      return h("button", { class: "btn btn-ghost btn-block mt", onClick: () => act(() => api.unfollow(id), "Unfollowed") }, icon("check", { size: 18 }), "Following");
    if (data.following === "pending")
      return h("button", { class: "btn btn-ghost btn-block mt", onClick: () => act(() => api.unfollow(id), "Request cancelled") }, "Requested — tap to cancel");
    return h("button", { class: "btn btn-primary btn-block mt", onClick: () => act(() => api.follow(id), data.user.is_private ? "Request sent" : "Following") }, icon("plus", { size: 18 }), data.user.is_private ? "Request to follow" : "Follow");
  }

  async function act(fn, msg) {
    try {
      await fn();
      toast(msg, "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function lockedBody() {
    return h("div", { class: "card center mt-lg" },
      h("div", { class: "empty-icon" }, icon("user", { size: 36 })),
      h("p", { class: "dim" }, data.user.is_private
        ? (data.following === "pending" ? "Waiting for approval to see their activity." : "This account is private. Follow to see their workouts and activity.")
        : "Follow to see their workouts and activity.")
    );
  }

  function viewableBody() {
    let presence;
    if (data.working_out) {
      const start = parseDate(data.active_since)?.getTime() ?? Date.now();
      const t = h("span", null, fmtDuration(Math.floor((Date.now() - start) / 1000)));
      timer = setInterval(() => { t.textContent = fmtDuration(Math.floor((Date.now() - start) / 1000)); }, 1000);
      presence = h("span", { class: "hstack", style: { gap: "6px", color: "var(--success)" } }, h("span", { class: "pulse" }), "Training now · ", t);
    } else {
      presence = h("span", { class: "dim" }, data.last_workout_at ? `Last trained ${relativeDay(data.last_workout_at).toLowerCase()}` : "No workouts yet");
    }

    return h("div", null,
      h("div", { style: { fontSize: ".86rem", margin: "12px 2px 0" } }, presence),
      h("div", { class: "stat-grid mt" },
        stat(String(data.total_sessions), "Workouts"),
        stat(String(data.week_sessions), "This week"),
        stat(fmtVolume(data.total_volume_kg).split(" ")[0], "Total volume")
      ),
      h("div", { class: "section-title" }, `Workouts (${data.workouts.length})`),
      data.workouts.length
        ? h("div", null, ...data.workouts.map(workoutCard))
        : h("div", { class: "card" }, h("p", { class: "dim center" }, "No shared workouts."))
    );
  }

  function workoutCard(w) {
    const primary = w.exercises.flatMap((e) => e.primary_muscle_ids || []);
    const mini = MuscleMap({ primary });
    mini.classList.add("mini");
    mini.style.maxWidth = "84px";
    return h("div", { class: "card card-tap", style: { marginBottom: "10px" }, onClick: () => navigate(`/discover/${w.id}`) },
      h("div", { class: "hstack", style: { alignItems: "flex-start", gap: "10px" } },
        mini,
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700 } }, w.name, w.is_public ? h("span", { class: "badge", style: { marginLeft: "8px" } }, "Public") : null),
          h("div", { class: "dim", style: { marginTop: "2px" } }, `${w.exercises.length} exercises`),
          h("div", { class: "dim", style: { marginTop: "4px", fontSize: ".8rem" } }, w.exercises.slice(0, 4).map((e) => e.exercise_name).join(" · ") + (w.exercises.length > 4 ? "…" : ""))
        ),
        icon("forward", { size: 18, className: "icon muted" })
      )
    );
  }

  function stat(num, label) {
    return h("div", { class: "stat" }, h("div", { class: "num accent" }, num), h("div", { class: "lbl" }, label));
  }

  // Stop the "training now" presence timer when navigating away (it was leaking before).
  onUnmount(root, () => { if (timer) clearInterval(timer); });

  return root;
}
